import { DoudianRecommendLogistics } from "@/dto/order/DouDianOrderExtraInfoDTO";
import OrderDTO from "@/dto/order/OrderDTO";
import { toMillisecondTimestamp } from "@/utils/time";
import { QuestionCircleFilled } from "@ant-design/icons";
import { Button, Popover } from "antd";
import { observer } from "mobx-react-lite";
import moment, { Moment } from "moment";
import React, { useEffect, useState } from "react";

interface DouDianRecommendLogisticsInfoProps {
    order: OrderDTO;
}

function calculateTimeDifference(laterMoment: Moment, earlierMoment: Moment, maxUnit: "day" | "hour") {
    try {
        const duration = moment.duration(laterMoment.diff(earlierMoment));

        const minutes = duration.minutes();
        const seconds = duration.seconds();
        if (maxUnit === "day") {
            const days = Math.floor(duration.asDays());
            const hours = duration.hours();

            return { days, hours, minutes, seconds };
        } else {
            const hours = Math.floor(duration.asHours());
            return { hours, minutes, seconds };
        }
    } catch {
        return {
            days: 0,
            hours: 0,
            minutes: 0,
            seconds: 0,
        };
    }
}

const DoudianRecommendLogisticsItem: React.FC<{
    doudianRecommendLogistics: DoudianRecommendLogistics;
}> = React.memo((props) => {
    const { doudianRecommendLogistics } = props;
    const [currentTime, updateCurrentTime] = useState(moment());
    const logisticsRecommendEndShipTime = toMillisecondTimestamp(
        doudianRecommendLogistics.logisticsRecommendEndShipTime,
    );

    const logisticsRecommendEndShipMoment = moment(logisticsRecommendEndShipTime);
    const isOverdue = currentTime.isAfter(logisticsRecommendEndShipMoment);

    useEffect(() => {
        if (!logisticsRecommendEndShipTime) return;

        /** 如果已超时，则按最小单位为分展示,否则是秒 */
        const timer = setTimeout(
            () => {
                updateCurrentTime(moment());
            },
            isOverdue ? 1000 * 60 : 1000,
        );

        return () => {
            clearTimeout(timer);
        };
    }, [logisticsRecommendEndShipTime, currentTime]);

    if (!logisticsRecommendEndShipTime) return null;

    function buildTimeInfo() {
        if (isOverdue) {
            const { days, hours, minutes } = calculateTimeDifference(
                currentTime,
                logisticsRecommendEndShipMoment,
                "day",
            );

            return (
                <span className="error fb ml5">
                    发货已超时{days}天{hours}小时{minutes}分钟
                </span>
            );
        } else {
            const { hours, minutes, seconds } = calculateTimeDifference(
                logisticsRecommendEndShipMoment,
                currentTime,
                "hour",
            );
            const diff = hours + "时" + minutes + "分" + seconds + "秒";

            return <span className="ml5">{diff}内发货</span>;
        }
    }

    return (
        <div className="small text-light mt5">
            <p>
                <span>{doudianRecommendLogistics.logisticsCompanyName}</span>-{" "}
                <span>{doudianRecommendLogistics.logisticsCompanyProductTypeName}</span>
                <Popover
                    content={
                        <span>
                            该快递为平台推荐快递公司，
                            承诺达订单请使用平台推荐的快递且在推荐发货时间前发货，否则晚到可能会产生赔付
                        </span>
                    }
                >
                    <QuestionCircleFilled className="ml5" />
                </Popover>
                {buildTimeInfo()}
            </p>
        </div>
    );
});

const DouDianRecommendLogisticsInfo: React.FC<DouDianRecommendLogisticsInfoProps> = function (props) {
    const { order } = props;
    const [showAll, setShowAll] = useState(false);

    if (!order.doudianOrderExtraInfo?.promiseDetail?.recommendLogisticsList?.length) return null;

    const recommendLogisticsList = order.doudianOrderExtraInfo.promiseDetail.recommendLogisticsList;

    const items = showAll ? recommendLogisticsList : recommendLogisticsList.slice(0, 1);

    return (
        <div>
            {items.map((doudianRecommendLogistics) => {
                const key =
                    doudianRecommendLogistics.logisticsCompanyName +
                    doudianRecommendLogistics.logisticsCompanyProductTypeName;

                return (
                    <DoudianRecommendLogisticsItem doudianRecommendLogistics={doudianRecommendLogistics} key={key} />
                );
            })}

            {recommendLogisticsList.length > 1 ? (
                <Button
                    type="link"
                    className="text-small"
                    style={{ padding: 0 }}
                    onClick={() => {
                        setShowAll(!showAll);
                    }}
                >
                    {showAll ? "收起" : "全部签约配送商发货时间"}
                </Button>
            ) : null}
        </div>
    );
};

export default observer(DouDianRecommendLogisticsInfo);
